/**
 * Utility to create default start nodes for an empty diagram
 * based on entity type configuration.
 */

import { DiagramNode, DiagramEdge } from "components/CC/Diagrams/ConnectionCreatorDiagram/ConnectionCreatorDiagram";
import { CCEntityType, CC_ENTITY_CONFIGS } from "components/CC/types/entityConfig";
import { NodeType } from "components/CC/types/enums";

import { getDefaultFormData } from "./nodeDefaults";

interface DefaultDiagramData {
    nodes: DiagramNode[];
    edges: DiagramEdge[];
}

/**
 * Creates default start node(s) for an empty diagram based on entity type.
 * Each entity type has a configured defaultFlowType that determines the start node type.
 *
 * The created structure matches what handleStartNodeTypeSelect creates:
 * - Start node with formData.flow = defaultFlowType
 * - Connected methods node (placeholder for next action)
 *
 * @param entityType - The CC entity type to create defaults for
 * @returns Object with nodes and edges arrays for the default diagram
 */
export const createDefaultStartNodes = (entityType: CCEntityType): DefaultDiagramData => {
    const config = CC_ENTITY_CONFIGS[entityType];
    const flowType = config.defaultFlowType;

    // Default positions for new diagram
    const startX = 100;
    const startY = 200;

    // Create IDs following flow-position format
    const startNodeId = "1-1";
    const methodsNodeId = "1-2";

    const startNode: DiagramNode = {
        id: startNodeId,
        type: NodeType.Start,
        position: { x: startX, y: startY },
        data: {
            formData: {
                ...getDefaultFormData(NodeType.Start),
                flow: flowType,
                beType: config.startBeType,
            },
        },
    };

    const methodsNode: DiagramNode = {
        id: methodsNodeId,
        type: NodeType.Methods,
        position: { x: startX + 200, y: startY },
        data: {},
    };

    const edge: DiagramEdge = {
        id: `edge-${startNodeId}-${methodsNodeId}`,
        source: startNodeId,
        target: methodsNodeId,
        type: "customEdge",
    };

    return {
        nodes: [startNode, methodsNode],
        edges: [edge],
    };
};
